/**
 * Utility functions for parsing and managing URL query parameters
 */

/**
 * Parse a boolean value from URL search params
 * @param value - The string value from URL params
 * @param defaultValue - Default value if param is missing
 * @returns boolean value
 */
export const parseBooleanParam = (value: string | null, defaultValue: boolean = false): boolean => {
  if (value === null || value === undefined) {
    return defaultValue;
  }
  return value === 'true' || value === '1';
};

/**
 * Parse a comma-separated string into an array
 * @param value - The string value from URL params (e.g., "all,webinar")
 * @param defaultValue - Default value if param is missing
 * @returns array of strings
 */
export const parseArrayParam = (value: string | null, defaultValue: string[] = []): string[] => {
  if (value === null || value === undefined || value === '') {
    return defaultValue;
  }
  return value.split(',').filter(Boolean);
};

/**
 * Parse a date string from URL params (ISO format: YYYY-MM-DD)
 * @param value - The string value from URL params
 * @param defaultValue - Default value if param is missing or invalid
 * @returns Date object or null
 */
export const parseDateParam = (value: string | null, defaultValue: Date | null = null): Date | null => {
  if (value === null || value === undefined || value === '') {
    return defaultValue;
  }
  // If the value is a date-only ISO string (YYYY-MM-DD), parse it as a local date
  // to avoid timezone shifts that can move the date backwards/forwards.
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1]);
    const month = Number(dateOnlyMatch[2]) - 1; // JS months are 0-based
    const day = Number(dateOnlyMatch[3]);
    const localDate = new Date(year, month, day);
    if (isNaN(localDate.getTime())) {
      return defaultValue;
    }
    return localDate;
  }

  // Fallback to native Date parsing for other formats
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) {
    return defaultValue;
  }
  return parsed;
};

/**
 * Format a Date object to ISO date string (YYYY-MM-DD)
 * @param date - Date object to format
 * @returns ISO date string or empty string if date is null
 */
export const formatDateParam = (date: Date | null): string => {
  if (!date) {
    return '';
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse a number from URL search params
 * @param value - The string value from URL params
 * @param defaultValue - Default value if param is missing or invalid
 * @returns number value
 */
export const parseNumberParam = (value: string | null, defaultValue: number | null = null): number | null => {
  if (value === null || value === undefined || value === '') {
    return defaultValue;
  }
  const num = Number(value);
  if (isNaN(num)) {
    return defaultValue;
  }
  return num;
};

/**
 * Update URL search params while preserving existing params
 * @param searchParams - Current URLSearchParams object
 * @param updates - Object with param keys and values to update
 * @param removeIfEmpty - If true, remove params with empty/null values
 * @returns New URLSearchParams object
 */
export const updateSearchParams = (
  searchParams: URLSearchParams,
  updates: Record<string, string | number | boolean | string[] | null | undefined>,
  removeIfEmpty: boolean = true
): URLSearchParams => {
  const newParams = new URLSearchParams(searchParams);
  
  Object.entries(updates).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      if (removeIfEmpty) {
        newParams.delete(key);
      }
    } else if (Array.isArray(value)) {
      if (value.length > 0) {
        newParams.set(key, value.join(','));
      } else if (removeIfEmpty) {
        newParams.delete(key);
      }
    } else if (typeof value === 'boolean') {
      newParams.set(key, value ? 'true' : 'false');
    } else if (typeof value === 'number') {
      newParams.set(key, value.toString());
    } else {
      newParams.set(key, value);
    }
  });
  
  return newParams;
};
